import "./style.scss";

import * as Sentry from "@sentry/react";
import type { CognitoUser as AWSCognitoUser } from "amazon-cognito-identity-js";
import Logo from "assets/img/svg/navbar/logo";
import { Auth, Hub } from "aws-amplify";
import AuthFirstLogin from "components/AuthFirstLogin";
import AuthForgotPassword from "components/AuthForgotPassword";
import AuthLogin from "components/AuthLogin";
import AuthLoginMFA from "components/AuthLoginMFA";
import { useTracker } from "contexts/tracker";
import { getCognitoErrorMessage, getCognitoUserAttribute, isExpectedCognitoError } from "helpers/utils";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { putMeVerifiedOrAcceptedTos, putMeVerifiedOrAcceptedTosBP } from "store/settingsStore";
import { useAppDispatch } from "store/store";

import loginRightPanelImg from "../../assets/img/login_right_panel.png";

type CognitoUser = AWSCognitoUser & {
    challengeParam?: {
        attributes?: {
            name: string;
        };
        userAttributes?: {
            email_verified: string;
        };
    };
};

const LogIn = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [username, setUsername] = useState("");
    const [isReadyToConnect, setIsReadyToConnect] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [isNewPasswordRequired, setIsNewPasswordRequired] = useState(false);
    const [currentUser, setCurrentUser] = useState<CognitoUser>();
    const [showPassword, setShowPassword] = useState(false);
    const [isPasswordForgotten, setIsPasswordForgotten] = useState(false);
    const [isResetPassword, setIsResetPassword] = useState(false);
    const [isReadyToVerifyEmail, setIsReadyToVerifyEmail] = useState(false);
    const [code, setCode] = useState("");
    const [MFAChallenge, setMFAChallenge] = useState(false);
    const [OTPCode, setOTPCode] = useState("");
    const tracker = useTracker();
    // Prevents a second Auth.signIn from being fired while one is still pending
    // which Amplify rejects with "Pending sign-in attempt already in progress".
    const signInInProgress = useRef(false);

    const captureAuthError = (step: string, error: any) => {
        if (isExpectedCognitoError(error)) return;
        Sentry.captureException(error, {
            tags: {
                flow: "login",
                auth_step: step,
                cognito_error: error?.code ?? error?.name ?? "unknown",
            },
            user: email ? { email } : undefined,
        });
    };

    const reportAuthError = (step: string, error: any) => {
        const errorMessage = getCognitoErrorMessage(error);
        captureAuthError(step, error);
        setErrorMessage(errorMessage);
        setError(true);
        setIsLoading(false);
        toast.error(errorMessage);
    };

    // A dead session is not a failed password attempt. Hub signOut and the
    // mount-time session check must leave the form idle, otherwise the button
    // stays pending and the empty error falls back to "wrong credentials".
    const resetLoginState = () => {
        setIsLoading(false);
        setError(false);
        setErrorMessage("");
    };

    /**
     * When using SSO, user is redirected to Amplify Hosted UI
     * and then comes back to the homepage with a `code` url params.
     *
     * Here we catch the code, sends it to the backend so we can get
     * Cognito tokens and finally enter the app
     */
    useEffect(() => {
        const unsubscribe = Hub.listen("auth", ({ payload: { event, data } }) => {
            switch (event) {
                case "signIn":
                    setCurrentUser(data);
                    navigate("/");
                    break;
                case "signOut":
                    resetLoginState();
                    break;
                case "customOAuthState":
                    /**
                     * Should never happen
                     */
                    console.log("customOAuthState", data);
                    break;
            }
        });

        getUser();

        return unsubscribe;
    }, []);

    const getUser = async (): Promise<void> => {
        try {
            const currentUser = await Auth.currentAuthenticatedUser();
            setUsername(currentUser.username);
            setCurrentUser(currentUser);
            setIsLoading(false);
        } catch {
            resetLoginState();
        }
    };

    const logIn = async () => {
        try {
            const user = await Sentry.startSpan(
                { name: "auth.signIn", op: "auth.login", forceTransaction: true, attributes: { flow: "login" } },
                () => Auth.signIn(email, password),
            );
            // Now that sign-in succeeded we know the account type — tag it so
            // login traces/replays can be filtered by beneficiary vs publisher.
            Sentry.setTag("accountType", getCognitoUserAttribute("custom:accountType", user) ?? "unknown");
            console.log("[LOGIN:challengeName]", user.challengeName);
            if (user.challengeName == "NEW_PASSWORD_REQUIRED") {
                setCurrentUser(user);
                setIsNewPasswordRequired(true);
                setIsLoading(false);
                setShowPassword(false);
                setError(false);
                return;
            } else if (user.challengeName === "SOFTWARE_TOKEN_MFA") {
                setCurrentUser(user);
                setIsNewPasswordRequired(false);
                setMFAChallenge(true);
                setIsLoading(false);
                setShowPassword(false);
                setError(false);
                return;
            }
            setUsername(getCognitoUserAttribute("name", user));
            return user;
        } catch (error) {
            console.log("[LOGIN]", error);
            reportAuthError("signIn", error);
            return null;
        }
    };

    const validateMFA = async () => {
        if (signInInProgress.current) return;
        signInInProgress.current = true;
        setIsLoading(true);
        setError(false);
        try {
            const updatedUser = await Sentry.startSpan(
                { name: "auth.confirmMFA", op: "auth.login", forceTransaction: true, attributes: { flow: "login" } },
                async () => {
                    const user = await Auth.signIn(email, password);
                    return Auth.confirmSignIn(user, OTPCode, "SOFTWARE_TOKEN_MFA");
                },
            );
            setUsername(getCognitoUserAttribute("name", updatedUser) ?? "");

            navigate("/");
            setTimeout(() => {
                window.location.reload();
            }, 1500);
        } catch (error) {
            reportAuthError("confirmMFA", error);
            return null;
        } finally {
            signInInProgress.current = false;
        }
    };

    const firstLogin = async () => {
        try {
            const user: CognitoUser | undefined = await Auth.completeNewPassword(currentUser, newPassword);
            setUsername(currentUser?.challengeParam?.attributes?.name ?? "");

            if (tracker) {
                tracker.trackEvent("Sign up", {});
            }

            return user;
        } catch (error: any) {
            reportAuthError("completeNewPassword", error);
        }
    };

    const emailVerification = async (user: CognitoUser) => {
        if (user.challengeParam?.userAttributes?.email_verified === "true") return;

        try {
            const verifyEmail = await Auth.verifyUserAttribute(user, "email");
            return verifyEmail;
        } catch (error) {
            reportAuthError("verifyEmail", error);
            return null;
        }
    };

    const emailVerificationSubmit = async (user?: CognitoUser) => {
        try {
            const verifyEmail = await Auth.verifyUserAttributeSubmit(user, "email", code);
            return verifyEmail;
        } catch (error) {
            reportAuthError("verifyEmailSubmit", error);
            return null;
        }
    };

    const forgotPassword = async () => {
        try {
            await Auth.forgotPassword(email);
            setIsResetPassword(true);
            setIsLoading(false);
            setError(false);
        } catch (error) {
            reportAuthError("forgotPassword", error);
        }
    };

    const resetPassword = async () => {
        try {
            await Auth.forgotPasswordSubmit(email, code.trim(), newPassword);
            return true;
        } catch (error) {
            reportAuthError("resetPassword", error);
            return false;
        }
    };

    const handleLogin = async () => {
        if (!email || !password) return;
        if (signInInProgress.current) return;
        signInInProgress.current = true;
        setIsLoading(true);
        try {
            const user = await logIn();
            // const userAttr = await Auth.userAttributes(user)
            if (user == null) return;
            navigate("/");
            setTimeout(() => {
                window.location.reload();
            }, 1500);
        } finally {
            signInInProgress.current = false;
        }
    };

    /**
     * Redirect to HostedUI
     */
    const handleSAMLLogin = async () => {
        setIsLoading(true);

        Auth.federatedSignIn();

        /**
         * Let's keep it for a while, we're not ready to let it go
         */
        // window.location.assign(
        //     `${import.meta.env.REACT_APP_SSO_ENDPOINT}?client_id=${encodeURIComponent(
        //         import.meta.env.REACT_APP_AWS_USER_POOL_WEB_CLIENT_ID,
        //     )}&response_type=code&scope=email+openid+profile&redirect_uri=${encodeURIComponent(
        //         window.location.origin + "/",
        //     )}`,
        // );
    };

    const handleFirstLogin = async () => {
        if (!newPassword) return;
        setIsLoading(true);
        const user = await firstLogin();
        if (user == null) return;

        /**
         * Bypass verification code on first login
         *
         */
        if (user?.challengeParam?.userAttributes?.email_verified === "true") {
            setCurrentUser(user);

            await handleEmailVerified(user);

            return;
        }

        await emailVerification(user);
        setIsLoading(false);
        setCurrentUser(user);
        setIsReadyToVerifyEmail(true);
    };

    const handleEmailVerified = async (user?: CognitoUser | null) => {
        /**
         * Show verification code only if account not yet verified
         */
        if (!user || !("challengeParam" in user) || user?.challengeParam?.userAttributes?.email_verified !== "true") {
            setIsLoading(true);
            const emailVerificationStatus = await emailVerificationSubmit(currentUser);
            if (emailVerificationStatus == null) return;
        }
        navigate("/");
        dispatch(putMeVerifiedOrAcceptedTos({ isVerified: true, hasAcceptedToS: true })).then((res) => {
            if (res.meta.requestStatus === "rejected")
                dispatch(putMeVerifiedOrAcceptedTosBP({ isVerified: true, hasAcceptedToS: true }));
        });
        setTimeout(() => {
            window.location.reload();
        }, 1500);
    };

    const handleForgottenPassword = async () => {
        if (!email) {
            toast.error(`${t("LogIn.EmailRequired")}`);
            return;
        }
        setIsLoading(true);
        await forgotPassword();
    };

    const handleResetPassword = async () => {
        if (!code.trim()) {
            toast.error(`${t("LogIn.CodeRequired")}`);
            return;
        }
        setIsLoading(true);
        const success = await resetPassword();
        if (!success) return;
        navigate("/");
        setTimeout(() => {
            window.location.reload();
        }, 1500);
    };

    const handleEmail = (e: any) => setEmail(e.target.value);

    const handlePassword = (e: any) => setPassword(e.target.value);

    const handleNewPassword = (e: any) => setNewPassword(e.target.value);

    const handleClickShowPassword = () => setShowPassword(!showPassword);

    const handleForgetPassword = () => {
        setIsPasswordForgotten(true);
        setError(false);
    };

    const handleCode = (e: any) => setCode(e.target.value);

    const handleVerificationCode = (e: any) => setCode(e.target.value);

    const handleGoBack = () => setIsPasswordForgotten(false);

    const handleResendCode = async () => {
        currentUser?.getAttributeVerificationCode("email", {
            onSuccess: () => {
                toast.success(`${t("LogIn.NewCodeSent")}`);
            },
            onFailure: () => {
                toast.error(`${t("Settings.SomethingWentWrong")}`);
            },
        });
    };

    useEffect(() => {
        if (email.length > 0 && password.length > 0) setIsReadyToConnect(true);
        else setIsReadyToConnect(false);
    }, [email.length, password.length]);

    const renderLogin = () => {
        const props = {
            username,
            email,
            showPassword,
            error,
            errorMessage,
            isReadyToConnect,
            isLoading,
            handleEmail,
            handlePassword,
            handleClickShowPassword,
            handleLogin,
            handleSAMLLogin,
            handleForgetPassword,
        };
        return <AuthLogin {...props} />;
    };

    const renderMFAChallenge = () => {
        const props = {
            username,
            error,
            errorMessage,
            isLoading,
            validateMFA,
            isReadyToConnect,
            setOTPCode,
        };
        return <AuthLoginMFA {...props} />;
    };

    const renderChangePassword = () => {
        const props = {
            username,
            newPassword,
            showPassword,
            error,
            errorMessage,
            isReadyToConnect,
            isLoading,
            isReadyToVerifyEmail,
            handleNewPassword,
            handleClickShowPassword,
            handleFirstLogin,
            handleVerificationCode,
            handleEmailVerified,
            handleResendCode,
        };
        return <AuthFirstLogin {...props} />;
    };

    const renderForgottenPassword = () => {
        const props = {
            username,
            showPassword,
            error,
            errorMessage,
            isResetPassword,
            isLoading,
            handleEmail,
            handleNewPassword,
            handleCode,
            handleClickShowPassword,
            handleGoBack,
            handleForgottenPassword,
            handleResetPassword,
            newPassword,
            email,
        };
        return <AuthForgotPassword {...props} />;
    };

    return (
        <div className="login">
            <div className="login-left">
                <div>
                    <Logo />
                </div>
                <div className="login-left-container">
                    {(!isPasswordForgotten &&
                        ((isNewPasswordRequired && renderChangePassword()) ||
                            (MFAChallenge && renderMFAChallenge()) ||
                            renderLogin())) ||
                        renderForgottenPassword()}
                </div>
                {/* {!isNewPasswordRequired &&
                    <div className="login-left-container-signup login-left-container-subtext">
                        {t("LogIn.NoAccount")}
                        <span>{t("LogIn.SignUp")}</span>
                    </div>
                } */}
            </div>
            <div className="login-right">
                <img src={loginRightPanelImg} alt="login-right-panel-image" />
            </div>
        </div>
    );
};

export default LogIn;
